import type { CabinetState, Snapshot } from '../shared/contract';

/**
 * THE SLIP PRINTER — how Der Croupier talks, and the only way he talks.
 *
 * ART-BIBLE §5 and §10 between them forbid the obvious thing. The Croupier does not get
 * a floating tooltip, a speech bubble, a modal, or a fourth typeface; the game's printed
 * matter is Share Tech Mono on `papier`, and the arcade is full of machines that already
 * spit paper at you. So he speaks the way the building speaks: a small slip ratchets out
 * of the cabinet's ticket slot, one line, and curls away.
 *
 * Three rules, all of them load-bearing rather than decorative:
 *
 * 1. **Never two at once.** Slips queue. A screen with two of his lines on it is a screen
 *    where he is nagging, and he does not nag — he is bored of you and wants his money.
 * 2. **Never mid-flight.** `hold()` lets the director refuse to print while a ball is in
 *    the air. A line that arrives while the player is watching physics is a line the
 *    player does not read, and the ART-BIBLE's whole juice budget is built on one thing
 *    happening at a time (§7.3).
 * 3. **≤ 8 words.** Enforced in `dev` by an assertion the E2E reads, because the rule is
 *    the character. Long sentences are a different man.
 *
 * The Hilfekarte at the bottom of this file is the opposite object and deliberately so:
 * the slip is what the game *says*, the Hilfekarte is what the player *asks for*. It is
 * on a held key, it is never pointed at, and it never appears on its own.
 */

type El = HTMLElement;

const h = (tag: string, cls?: string, text?: string): El => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

/** ART-BIBLE §7.1. Printed matter ratchets; it does not fade in. */
const RATCHET = 55;
const BEAT = 500;

export interface SlipOpts {
  root: El;
  /** how long a slip stays up before it curls away, in ms */
  dwell?: number;
}

export interface SlipLine {
  /** Der Croupier's line. German, deadpan, ≤ 8 words. */
  text: string;
  /** an optional second row in smaller type — a key prompt, never a second sentence */
  prompt?: string;
  /** a clickable option printed under the line (the door's "Ich kenne mich aus.") */
  choice?: { label: string; on: () => void };
  /** red slip: the House taking something */
  cold?: boolean;
}

/**
 * A card that is **pinned to the cabinet** rather than spoken.
 *
 * The distinction is not decoration. A queued slip waits its turn, which is right for a
 * voice — he says one thing at a time. It is exactly wrong for an *option*: the door's
 * "Ich kenne mich aus." queued behind two lines of fiction meant a player who already
 * knows this game had to sit through eight seconds of being told about a marker before
 * they were allowed to decline it, which is the one thing that out exists to prevent.
 *
 * So an option is pinned: it appears at once, it does not expire, it does not block the
 * queue, and it comes down when the thing it offers is no longer on the table.
 */
export interface PinnedCard {
  text: string;
  label: string;
  on: () => void;
  note?: string;
}

/** How many words a Croupier line may be. The character, as a number. */
export const MAX_WORDS = 8;

export const wordCount = (s: string): number => s.trim().split(/\s+/).filter(Boolean).length;

export class SlipPrinter {
  readonly el: El;
  private readonly dwell: number;
  private readonly queueEl: El;
  private readonly pinEl: El;
  private queue: SlipLine[] = [];
  private printing = false;
  private held = false;
  private heldSince = 0;
  private timers: number[] = [];
  /** every line printed this session, in order — the E2E reads this */
  readonly log: string[] = [];

  constructor(o: SlipOpts) {
    this.el = h('div', 'sh-slips');
    this.pinEl = h('div', 'sh-slips-pin');
    this.queueEl = h('div', 'sh-slips-queue');
    this.el.append(this.pinEl, this.queueEl);
    /**
     * 2.6 s, measured against a played run rather than chosen.
     *
     * The first draft sat at 3.4 s plus a BEAT, and the contrarian playtest found what
     * that costs: with two lines queued at the top of a beat, Der Croupier was running
     * four seconds behind the player's hands — the player shoved the machine, the machine
     * tilted, and his comment on it arrived after they had already moved on. A deadpan
     * character is late on purpose by half a beat, not by four seconds.
     */
    this.dwell = o.dwell ?? 2600;
    o.root.appendChild(this.el);
  }

  /** Pin an option to the cabinet. One at a time; pinning replaces whatever was there. */
  pin(card: PinnedCard): void {
    this.pinEl.innerHTML = '';
    const slip = h('div', 'sh-slip sh-slip-pinned');
    const body = h('div', 'sh-slip-body');
    body.appendChild(h('div', 'sh-slip-line', card.text));
    if (card.note) body.appendChild(h('div', 'sh-slip-prompt', card.note));
    const b = h('button', 'sh-slip-choice', card.label);
    b.addEventListener('click', () => card.on());
    body.appendChild(b);
    slip.appendChild(body);
    this.pinEl.appendChild(slip);
    this.log.push(card.text);
  }

  /** Take the pinned card down. The thing it offered is no longer on the table. */
  unpin(): void {
    const cur = this.pinEl.firstElementChild;
    if (!cur) return;
    cur.classList.add('out');
    this.later(() => this.pinEl.innerHTML = '', BEAT);
  }

  /**
   * Queue a line. Returns false and prints nothing if the line breaks the voice rule —
   * loudly in the console, because a silently-dropped tutorial line is a tutorial with a
   * hole in it and a silently-printed nine-word line is a different character.
   */
  say(line: SlipLine): boolean {
    if (wordCount(line.text) > MAX_WORDS) {
      console.error(
        `SLIP REFUSED — Der Croupier does not use ${wordCount(line.text)} words: "${line.text}"`,
      );
      return false;
    }
    this.queue.push(line);
    this.pump();
    return true;
  }

  /**
   * Physics is happening. Hold the paper until it stops — but not forever.
   *
   * A player throwing continuously keeps a ball in the air continuously, and an
   * unconditional hold means Der Croupier never gets a word in for as long as they keep
   * playing. Measured: he went silent for four throws in a row during the contrarian pass
   * while the beat waiting for him to finish speaking waited on him to start. The hold is
   * a courtesy, so it has a deadline.
   */
  hold(on: boolean): void {
    if (on && !this.held) this.heldSince = Date.now();
    this.held = on;
    // The director calls this every frame, which is what makes the deadline effective:
    // once it passes, the next frame pumps even though nothing "changed".
    if (!this.holding) this.pump();
  }

  /** How long a line may be kept waiting for a quiet moment before it takes one. */
  private static readonly MAX_HOLD_MS = 4000;

  private get holding(): boolean {
    return this.held && Date.now() - this.heldSince < SlipPrinter.MAX_HOLD_MS;
  }

  /** Is a slip on screen or waiting to be? */
  get busy(): boolean {
    return this.printing || this.queue.length > 0;
  }

  clear(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
    this.queue = [];
    this.printing = false;
    this.pinEl.innerHTML = '';
    this.queueEl.innerHTML = '';
  }

  dispose(): void {
    this.clear();
    this.el.remove();
  }

  private later(fn: () => void, ms: number): void {
    this.timers.push(setTimeout(fn, ms) as unknown as number);
  }

  private pump(): void {
    if (this.printing || this.holding || this.queue.length === 0) return;
    const line = this.queue.shift()!;
    this.printing = true;
    this.log.push(line.text);

    const slip = h('div', 'sh-slip');
    slip.classList.toggle('cold', line.cold === true);
    const body = h('div', 'sh-slip-body');
    slip.appendChild(body);
    this.queueEl.appendChild(slip);

    // The line ratchets out a character at a time at RATCHET-per-few-chars: a till roll
    // moves in detents, and the detent is the sound of the joke landing.
    const text = line.text;
    let i = 0;
    const row = h('div', 'sh-slip-line');
    body.appendChild(row);
    const step = (): void => {
      i = Math.min(text.length, i + 2);
      row.textContent = text.slice(0, i);
      if (i < text.length) {
        this.later(step, RATCHET / 2);
        return;
      }
      if (line.prompt) body.appendChild(h('div', 'sh-slip-prompt', line.prompt));
      if (line.choice) {
        const b = h('button', 'sh-slip-choice', line.choice.label);
        b.addEventListener('click', () => line.choice!.on());
        body.appendChild(b);
      }
      // hold a BEAT of stillness before the paper is allowed to leave. ART-BIBLE §10:
      // "cut motion with stillness — a BEAT before a payout is worth more than another
      // particle." The same is true of a punchline.
      const stay = line.choice ? this.dwell * 4 : this.dwell;
      this.later(() => {
        slip.classList.add('out');
        this.later(() => {
          slip.remove();
          this.printing = false;
          this.pump();
        }, BEAT);
      }, stay + BEAT);
    };
    step();
  }

  /** Take the current slip away early — the player did the thing, the line is stale. */
  advance(): void {
    const cur = this.queueEl.firstElementChild;
    if (!cur || cur.classList.contains('out')) return;
    cur.classList.add('out');
    this.later(() => {
      cur.remove();
      this.printing = false;
      this.pump();
    }, BEAT);
  }
}

// ---------------------------------------------------------------------------------
// DIE HILFEKARTE

const VERBS: readonly [string, string][] = [
  ['Setzen', 'Maus zielen · Klick oder Leertaste'],
  ['Rütteln', 'R — der Automat wackelt, der TILT-Zeiger steigt'],
  ['GLÜCK', 'G — biegt EIN angesagtes Ereignis'],
  ['Kassieren / Nachlegen', 'K nimmt mit · N verdoppelt'],
];

const ELSEWHERE: readonly [string, string][] = [
  ['E · Esc', 'an den Automaten treten, und wieder weg'],
  ['A · D', 'den Saal entlanggehen (am Flipper: die Schläger)'],
  ['I halten', 'die echten Quoten, nach allen Modifikatoren'],
  ['F · B', 'auf Zinken prüfen · Brecheisen ansetzen'],
  ['V', 'Die Vitrine'],
  ['H halten', 'diese Karte'],
];

/**
 * The cabinet's own Hilfekarte, on a held key.
 *
 * This is the reference layer, and it is deliberately *not* part of the lesson: the
 * Lehrnacht never points at it. A tutorial that teaches you where the manual is has spent
 * its most expensive minutes teaching you to read instead of to play. Players find this,
 * or the one-time first-encounter slip mentions it once and never again.
 *
 * What it shows is the machine you are standing at — its declared tag table and its
 * GRENZE, straight out of `CabinetState`, which is the same data the board prints. There
 * is no second copy of the rules in this file, because a second copy is a copy that goes
 * stale.
 */
export class Hilfekarte {
  readonly el: El;
  private shown = false;
  private cab: CabinetState | null = null;

  constructor(root: El) {
    this.el = h('div', 'sh-hilfe');
    root.appendChild(this.el);
  }

  observe(s: Snapshot): void {
    this.cab = s.cabinet;
    if (this.shown) this.render();
  }

  get isOpen(): boolean {
    return this.shown;
  }

  show(on: boolean): void {
    if (on === this.shown) return;
    this.shown = on;
    this.el.classList.toggle('on', on);
    if (on) this.render();
  }

  dispose(): void {
    this.el.remove();
  }

  private render(): void {
    this.el.innerHTML = '';
    const card = h('div', 'sh-hilfe-card');
    card.append(
      h('div', 'sh-hilfe-title', 'HILFEKARTE'),
      h('div', 'sh-hilfe-sub', 'Bitte nach Gebrauch zurücklegen.'),
    );

    const verbs = h('div', 'sh-hilfe-sect');
    verbs.appendChild(h('h4', undefined, 'DIE VIER HANDGRIFFE'));
    for (const [k, v] of VERBS) verbs.appendChild(row(k, v));
    card.appendChild(verbs);

    const rest = h('div', 'sh-hilfe-sect');
    rest.appendChild(h('h4', undefined, 'ALLES ANDERE'));
    for (const [k, v] of ELSEWHERE) rest.appendChild(row(k, v));
    card.appendChild(rest);

    const cab = this.cab;
    const machine = h('div', 'sh-hilfe-sect');
    if (!cab) {
      machine.appendChild(h('h4', undefined, 'DIESER AUTOMAT'));
      machine.appendChild(h('div', 'sh-hilfe-none', 'Sie stehen an keinem. Treten Sie an einen heran.'));
    } else {
      machine.appendChild(h('h4', undefined, `DIESER AUTOMAT · ${cab.machine.toUpperCase()}`));
      if (cab.helpCard.length === 0) {
        machine.appendChild(h('div', 'sh-hilfe-none', 'Nichts Erklärtes. Hier zählt nur Physik.'));
      }
      for (const line of cab.helpCard) machine.appendChild(row(line.tag, line.note));
      machine.appendChild(h('div', 'sh-hilfe-grenze', `GRENZE: ${cab.limitLabel}`));
    }
    card.appendChild(machine);
    this.el.appendChild(card);
  }
}

function row(k: string, v: string): El {
  const e = h('div', 'sh-hilfe-row');
  e.append(h('span', 'sh-hilfe-k', k), h('span', 'sh-hilfe-v', v));
  return e;
}
